import React, { useEffect, useState } from 'react';
import { Button, Table, Tag, Modal, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useUserAssets, useAssetActions } from '../../../../../hooks/useAssets';
import { Asset, AssetStatus } from '../../../../../types/asset.types';
import { User } from '../../../../../types/user.types';
import CreateAssetModal from '../../../assets/components/CreateAssetModal';
import UpdateAssetModal from '../../../assets/components/UpdateAssetModal';

const { confirm } = Modal;

interface EmployeeAssetsProps {
  userData: User;
}

const EmployeeAssets: React.FC<EmployeeAssetsProps> = ({ userData }) => {
  const { userAssets, loading, fetchUserAssets } = useUserAssets(userData.id);
  const { deleteAsset } = useAssetActions();
  
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  useEffect(() => {
    fetchUserAssets();
  }, [fetchUserAssets]);

  const handleDelete = async (assetId: number) => {
    try {
      await deleteAsset(assetId);
      fetchUserAssets(); // Listeyi yenile
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const showDeleteConfirm = (asset: Asset) => {
    confirm({
      title: 'Zimmeti silmek istediğinize emin misiniz?',
      icon: <ExclamationCircleOutlined />,
      content: `${asset.name} (${asset.serialNumber}) kalıcı olarak silinecek.`,
      okText: 'Evet, Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk() {
        handleDelete(asset.id);
      },
    });
  };

  const handleStatusUpdate = (asset: Asset) => {
    setSelectedAsset(asset);
    setUpdateModalVisible(true);
  };

  const getStatusColor = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.ASSIGNED:
        return 'processing';
      case AssetStatus.RETURNED:
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.ASSIGNED:
        return 'Atanmış';
      case AssetStatus.RETURNED:
        return 'İade Edildi';
      default:
        return status;
    }
  };

  const columns = [
    {
      title: 'Zimmet Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Asset, b: Asset) => a.name.localeCompare(b.name),
    },
    {
      title: 'Seri Numarası',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      sorter: (a: Asset, b: Asset) => a.serialNumber.localeCompare(b.serialNumber),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: AssetStatus) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
      sorter: (a: Asset, b: Asset) => a.status.localeCompare(b.status),
    },
    {
      title: 'Zimmet Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
      sorter: (a: Asset, b: Asset) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'İade Tarihi',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string, record: Asset) => {
        if (record.status === AssetStatus.RETURNED) {
          return new Date(date).toLocaleDateString('tr-TR');
        }
        return '-';
      },
      sorter: (a: Asset, b: Asset) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (text: any, record: Asset) => (
        <div>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleStatusUpdate(record)}
            style={{ marginRight: 8 }}
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => showDeleteConfirm(record)}
            danger
          />
        </div>
      ),
    },
  ];

  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
    fetchUserAssets();
    message.success('Zimmet başarıyla oluşturuldu');
  };

  const handleUpdateSuccess = () => {
    setUpdateModalVisible(false);
    setSelectedAsset(null);
    fetchUserAssets();
    message.success('Zimmet durumu güncellendi');
  };

  // İstatistikler
  const totalAssets = userAssets?.length || 0;
  const assignedAssets = userAssets?.filter(asset => asset.status === AssetStatus.ASSIGNED).length || 0;
  const returnedAssets = userAssets?.filter(asset => asset.status === AssetStatus.RETURNED).length || 0;

  return (
    <div className="accordion-item">
      <div className="accordion-header" id="headingAssets">
        <div className="accordion-button" role="button" data-bs-toggle="collapse" data-bs-target="#collapseAssets" style={{ cursor: 'pointer' }}>
          <div className="d-flex align-items-center justify-content-between flex-fill">
            {/* <i className="ti ti-archive me-2"></i> */}
            <h5 className="mb-0">Zimmet Yönetimi</h5>
            <div className="d-flex">
              <i className="ti ti-chevron-down fs-18" />
            </div>
          </div>
        </div>
      </div>
      <div id="collapseAssets" className="accordion-collapse collapse" data-bs-parent="#accordionExample">
        <div className="accordion-body">
          {/* İstatistikler */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <span className="avatar avatar-xs bg-primary me-2">
                      <i className="ti ti-archive text-white"></i>
                    </span>
                    <h4 className="mb-0">{totalAssets}</h4>
                  </div>
                  <p className="text-muted mb-0">Toplam Zimmet</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <span className="avatar avatar-xs bg-warning me-2">
                      <i className="ti ti-user-check text-white"></i>
                    </span>
                    <h4 className="mb-0">{assignedAssets}</h4>
                  </div>
                  <p className="text-muted mb-0">Atanmış</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center p-3">
                  <div className="d-flex align-items-center justify-content-center mb-2">
                    <span className="avatar avatar-xs bg-success me-2">
                      <i className="ti ti-check text-white"></i>
                    </span>
                    <h4 className="mb-0">{returnedAssets}</h4>
                  </div>
                  <p className="text-muted mb-0">İade Edildi</p>
                </div>
              </div>
            </div>
          </div>

          {/* Yeni Zimmet Butonu */}
          <div className="d-flex justify-content-end mb-3">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Yeni Zimmet Ekle
            </Button>
          </div>

          {/* Zimmet Tablosu */}
          <Table
            columns={columns}
            dataSource={userAssets || []}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 5,
              size: 'small',
              showSizeChanger: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
            }}
            size="small"
          />

          {/* Modals */}
          <CreateAssetModal
            visible={createModalVisible}
            onClose={() => setCreateModalVisible(false)}
            onSuccess={handleCreateSuccess}
            defaultUser={userData}
          />

          <UpdateAssetModal
            visible={updateModalVisible}
            onClose={() => setUpdateModalVisible(false)}
            onSuccess={handleUpdateSuccess}
            asset={selectedAsset}
          />
        </div>
      </div>
    </div>
  );
};

export default EmployeeAssets; 